import React from 'react';
import { APITypes } from '../../../primary-record-types';
import * as Card from '../../v2BaseComponents/Card';
import { getIntl } from '../../../utils/intl';
import { oneUpEndpointAddressToString } from '../../../utils/addressToString';
import useOneUpHealthEndpoint from '../../../hooks/api/useOneUpEndpoint';
import Stack from '../../v2BaseComponents/Stack';
import getDateString from '../../../utils/getDateString';

const intl = getIntl('profile-add-patient-portal-card-alt', {
    alt: 'Health system logo',
    disabled: 'Connection Disabled',
    automaticRefreshLapsed: 'Automatic refresh expired on {date}',
    automaticallyRefreshingThrough: 'Automatically refreshing through {date}',
});

export type EndpointCardOwnProps = {
    endpoint: APITypes.ehrIntegrationsService.OneUpHealthEndpointWithDisabledStatus;
    onPress?: () => void;
    isDisabled?: boolean;
    subText?: string;
    portalPatientName?: string;
    omitAddress?: boolean;
    mutedSubText?: string;
    subHeader?: string;
    isDisabledEndpoint?: boolean;
    automaticRefreshExpirationDateTime?: string;
    userDisabledAutomaticRefresh?: boolean;
    isFreeUser?: boolean;
    hideAutomaticRefreshMessage?: boolean;
};

export type EndpointCardProps = EndpointCardOwnProps;

const EndpointCard: React.FC<EndpointCardProps> = (props) => {
    const isAutoRefreshingEndpoint =
        !!props.automaticRefreshExpirationDateTime && !props.isDisabledEndpoint && !props.userDisabledAutomaticRefresh;
    const hasAutomaticRefreshLapsed =
        isAutoRefreshingEndpoint &&
        new Date(props.automaticRefreshExpirationDateTime!).getTime() < new Date().getTime();

    const getRefreshContent = () => {
        if (!isAutoRefreshingEndpoint || props.isFreeUser || props.hideAutomaticRefreshMessage) return null;

        if (hasAutomaticRefreshLapsed) {
            return (
                <Card.CardBody2 color="Alert">
                    {intl('automaticRefreshLapsed', {
                        date: getDateString(props.automaticRefreshExpirationDateTime!, 'date-time'),
                    })}
                </Card.CardBody2>
            );
        } else {
            return (
                <Card.CardBody2 color="Success">
                    {intl('automaticallyRefreshingThrough', {
                        date: getDateString(props.automaticRefreshExpirationDateTime!, 'date-time'),
                    })}
                </Card.CardBody2>
            );
        }
    };
    return (
        <Card.Card onPress={props.onPress} isDisabled={props.isDisabled} testID="endpoint-card">
            <Card.CardRightContent
                rightContent={
                    <Stack vertical justify="center">
                        {props.endpoint.logo ? (
                            <Card.CardImage
                                size={{
                                    width: 84,
                                    height: 84,
                                }}
                                url={props.endpoint.logo}
                                alt={intl('alt')}
                            />
                        ) : null}
                    </Stack>
                }
            >
                <Card.VerticalSplitCardContent>
                    <Card.CardContent>
                        <Card.CardHeader>{props.endpoint.name}</Card.CardHeader>
                        <Card.CardBody>{props.portalPatientName}</Card.CardBody>
                        {props.endpoint.isDisabled ? <Card.CardChip title={intl('disabled')} /> : null}
                    </Card.CardContent>
                    <Card.CardContent>
                        {getRefreshContent()}
                        <Card.CardBody2>{props.subText}</Card.CardBody2>
                        {!props.omitAddress ? (
                            <Card.CardBody2>{oneUpEndpointAddressToString(props.endpoint)}</Card.CardBody2>
                        ) : null}
                        <Card.CardBody2>{props.mutedSubText}</Card.CardBody2>
                    </Card.CardContent>
                </Card.VerticalSplitCardContent>
            </Card.CardRightContent>
        </Card.Card>
    );
};

export default React.memo(EndpointCard);

export type UnloadedEndpointCardProps = Omit<EndpointCardProps, 'endpoint'> & {
    endpointId: string | number;
    skeletonFallback?: boolean;
};

export const UnloadedEndpointCard: React.FC<UnloadedEndpointCardProps> = (props) => {
    const { endpoint, isLoading } = useOneUpHealthEndpoint({
        endpointId: props.endpointId,
    });

    if (isLoading || !endpoint) {
        return null;
    }

    return <EndpointCard {...props} endpoint={endpoint} isDisabled={false} />;
};
